import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchDebateReplay, fetchV2ResilienceSnapshot } from "../transport/authClient";
import type {
  ConsensusTrajectoryPoint,
  DebateReplayData,
  PersonaContributionStats,
  ReplayDissent,
  ReplayPersona,
  ReplayTurn,
  V2ResilienceSnapshot,
} from "../transport/authClient";
import { AppShell } from "../components/layout/AppShell";
import { openVerdictReport } from "../utils/printableVerdict";
import VersionsTab from "../components/verdict/VersionsTab";
import RerunVersionModal from "../components/versions/RerunVersionModal";
import { useDebateStore } from "../store/debateStore";
import { adaptReplayCore } from "../domain/replayAdapter";
import type {
  ActionItem,
  DebateTopology,
  KpiEntry,
  Persona,
  RiskEntry,
  Turn,
  Verdict,
} from "../domain/types";
import { coerceActionPlan } from "../domain/types";
import type {
  DebateAggregateMetrics,
  FlatRagCitation,
  FlatResearchSource,
  SpeakerStat,
} from "../store/selectors";

// Bilişsel Profil, canlı konsolda olduğu gibi replay'de de açılabilir.
// Ağırlığı (beyin SVG'si + lob panelleri) yalnızca operatör bir üyeye
// tıkladığında ödenmeli — bu yüzden canlı sayfadaki gibi lazy.
const BrainModal = lazy(() =>
  import("../components/brain/BrainModal").then((m) => ({
    default: m.BrainModal,
  })),
);
const CouncilQaTab = lazy(() => import("../components/verdict/CouncilQaTab"));

/* ════════════════════════════════════════════════════════════════════
   ReplayPage — Post-debate forensic report
   ────────────────────────────────────────────────────────────────────
   Tabbed deep-dive over a finalised session:
     • Özet         — executive summary, headline KPIs, top dissents
     • Müzakere     — round-grouped transcript, critique surfaces
     • Trajektori   — consensus-over-rounds line chart
     • Eylem Planı  — 3-column architectural / strategic / tactical
     • Katılımcılar — per-persona contribution stats + bar
     • Simülasyon   — chaos-rollout outcomes table

   Pure presentational; all data comes from the single
   `GET /api/debate/history/{id}` round-trip.
   ════════════════════════════════════════════════════════════════════ */

type TabId =
  | "summary"
  | "ask"
  | "commentary"
  | "transcript"
  | "trajectory"
  | "plan"
  | "sources"
  | "personas"
  | "simulation"
  | "versions";

const TABS: Array<{ id: TabId; label: string; icon: string }> = [
  { id: "summary", label: "Özet", icon: "M9 12l2 2 4-4M7 7h10v10H7z" },
  {
    // Arşiv sorgulama, geçmişten açılan tartışmalarda CANLI olanlardan
    // daha değerli: operatör aylar önceki bir kararı yeniden okurken
    // bağlamı hatırlamıyor ve sayfalarca raporu tekrar taramak yerine
    // doğrudan sorabiliyor.
    id: "ask",
    label: "Konseye Sor",
    // Chat bubble with question mark
    icon: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  {
    id: "commentary",
    label: "Yorum",
    // Open book — editorial commentary
    icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
  },
  {
    id: "transcript",
    label: "Müzakere",
    icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
  },
  {
    id: "trajectory",
    label: "Trajektori",
    icon: "M3 17l6-6 4 4 8-8M14 7h7v7",
  },
  {
    id: "plan",
    label: "Eylem Planı",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  },
  {
    id: "sources",
    label: "Kaynaklar",
    // Document with magnifier — dual-purpose icon for the bibliography
    // (web sources + uploaded documents) tab.
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  },
  {
    id: "personas",
    label: "Katılımcılar",
    icon: "M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-2a4 4 0 100-8 4 4 0 000 8zm6 0a4 4 0 100-8 4 4 0 000 8z",
  },
  {
    id: "simulation",
    label: "Simülasyon",
    icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z",
  },
  {
    id: "versions",
    label: "Versiyonlar",
    // Git branch glyph — emphasises lineage/forking semantics.
    icon: "M6 3v12m0 0a3 3 0 100 6 3 3 0 000-6zm0-12a3 3 0 110 6 3 3 0 010-6zm12 0a3 3 0 110 6 3 3 0 010-6zM18 9a9 9 0 01-9 9",
  },
];

/* ───────────────────── helpers ───────────────────── */

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}dk ${s}s` : `${s}s`;
}

function formatPercentValue(x: number | null | undefined): string {
  if (x == null || !Number.isFinite(x)) return "—";
  return `${Math.round(x * 100)}%`;
}

function ResilienceSnapshotBanner({ snapshot }: { snapshot: V2ResilienceSnapshot }) {
  const baseline = snapshot.baseline_simulation_score;
  const hardened = snapshot.hardened_simulation_score;
  if (baseline == null && hardened == null && snapshot.status === "baseline_only") {
    return null;
  }
  const uplift =
    typeof baseline === "number" && typeof hardened === "number"
      ? hardened - baseline
      : null;

  return (
    <div className="mb-5 rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-5 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-full bg-cyan-400/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-200">
          Dayanıklılık Analizi
        </span>
        <span className="text-sm text-slate-300">Durum: {resilienceStatusLabel(snapshot.status)}</span>
        <span className="text-sm text-slate-300">
          İlk plan: {formatPercentValue(baseline)}
        </span>
        <span className="text-sm text-slate-300">
          Güçlendirilmiş plan: {formatPercentValue(hardened)}
        </span>
        {uplift !== null && (
          <span className={uplift >= 0 ? "text-sm text-emerald-300" : "text-sm text-red-300"}>
            Fark: {uplift >= 0 ? "+" : ""}
            {formatPercentValue(uplift)}
          </span>
        )}
      </div>
      <p className="mt-2 text-sm text-slate-400">
        Karar planının stres testi sonucu ve güçlendirme sonrası dayanıklılık
        farkı burada izlenir.
      </p>
    </div>
  );
}

function resilienceStatusLabel(status: string): string {
  switch (status) {
    case "baseline_only":
      return "İlk stres testi";
    case "completed":
      return "Güçlendirme tamamlandı";
    case "regression_rejected":
      return "Güçlendirme reddedildi";
    case "insufficient_evidence":
      return "Kanıt yetersiz";
    default:
      return status;
  }
}

function formatNumber(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("tr-TR");
}

function consensusTone(score: number | null | undefined): {
  text: string;
  bg: string;
  ring: string;
  label: string;
} {
  if (score == null)
    return {
      text: "text-slate-400",
      bg: "bg-slate-500/10",
      ring: "ring-slate-500/20",
      label: "Skor yok",
    };
  if (score >= 70)
    return {
      text: "text-emerald-300",
      bg: "bg-emerald-500/10",
      ring: "ring-emerald-500/30",
      label: "Güçlü Mutabakat",
    };
  if (score >= 40)
    return {
      text: "text-amber-300",
      bg: "bg-amber-500/10",
      ring: "ring-amber-500/30",
      label: "Kısmi Mutabakat",
    };
  return {
    text: "text-rose-300",
    bg: "bg-rose-500/10",
    ring: "ring-rose-500/30",
    label: "Kritik Ayrışma",
  };
}

function severityChip(severity?: string) {
  const sev = (severity ?? "minor").toLowerCase();
  if (sev === "blocker")
    return {
      label: "BLOKER",
      className: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    };
  if (sev === "major")
    return {
      label: "ÖNEMLİ",
      className: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    };
  return {
    label: "MİNÖR",
    className: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  };
}

/* ════════════════════════════════════════════════════════════════════
   Replay → VerdictReportInput adapter
   ────────────────────────────────────────────────────────────────────
   The print pipeline (`openVerdictReport`) was built around the live
   `Verdict` / `Persona` / `Turn` shapes streamed via SSE. The replay
   API returns a flatter, snake_cased projection. This adapter widens
   the replay rows back into the live shape without forcing the print
   module to know about two contracts.

   Missing-field policy:
     • Optional fields default to `null` / `[]`.
     • Persona fields not surfaced by the replay API (asymmetry,
       voiceId, themeColor, seatIndex, gender) get harmless defaults
       so the print template's optional render guards pass.
   ════════════════════════════════════════════════════════════════════ */

function adaptReplayToVerdictReport(
  data: DebateReplayData,
): {
  query: string;
  topology: DebateTopology;
  startedAt: number | null;
  verdict: Verdict;
  personas: Persona[];
  turns: Turn[];
  consensusScore?: number;
  metrics?: DebateAggregateMetrics;
  sources?: FlatResearchSource[];
  ragCitations?: FlatRagCitation[];
} {
  // Persona/turn/verdict shaping is shared with `sessionSlice.resume()`
  // (İş Kolu — arka planda devam eden tartışma) via `replayAdapter.ts` —
  // see that module's docstring for why this must be single-sourced.
  const core = adaptReplayCore(data);
  const { personas, turns } = core;
  const verdict: Verdict = core.verdict ?? {
    executiveSummary: "",
    dissentingLog: [],
    actionPlan: coerceActionPlan({}),
    generatedAt: data.session.ended_at
      ? new Date(data.session.ended_at).getTime()
      : Date.now(),
    simulationScore: null,
    simulationOutcomes: [],
    commentaryAnalysis: null,
    commentaryCritique: null,
  };

  // ── Hydrate the bibliography sections of the PDF. The print
  // template was already coded against `FlatResearchSource[]` and
  // `FlatRagCitation[]` (live-debate selectors), so we adapt the
  // backend rollups into the same shape — keeps the printable
  // module independent from the replay/live-store dichotomy.
  const personaIndex = new Map(personas.map((p) => [p.id, p]));
  const turnIndex = new Map(turns.map((t) => [t.id, t]));

  const flatSources: FlatResearchSource[] = (
    data.sources_rollup ?? []
  ).map((rollup) => {
    const firstTurnId = rollup.cited_in_turns[0];
    const turn = firstTurnId ? turnIndex.get(firstTurnId) : undefined;
    const persona = turn ? personaIndex.get(turn.speakerId) : undefined;
    return {
      speakerId: persona?.id ?? "",
      speakerName: persona?.displayName ?? "Konsey",
      themeColor: persona?.themeColor ?? null,
      query: "",
      source: {
        name: rollup.name,
        title: rollup.title,
        url: rollup.url,
        summary: rollup.summary ?? "",
        published: rollup.published ?? null,
      },
    };
  });

  const flatRag: FlatRagCitation[] = (
    data.documents_rollup ?? []
  ).flatMap((doc) => {
    const firstTurnId = doc.cited_in_turns[0];
    const turn = firstTurnId ? turnIndex.get(firstTurnId) : undefined;
    const persona = turn ? personaIndex.get(turn.speakerId) : undefined;
    return doc.chunks.map((c) => ({
      speakerId: persona?.id ?? "",
      speakerName: persona?.displayName ?? "Konsey",
      themeColor: persona?.themeColor ?? null,
      query: "",
      citation: {
        docId: c.doc_id,
        filename: c.filename,
        chunkIdx: c.chunk_idx,
        score: c.score,
        scope: c.scope,
        excerpt: c.excerpt,
      },
      reuseCount: doc.cited_in_turns.length,
    }));
  });

  const metrics = buildReplayMetrics(data, personas, flatSources.length, flatRag.length);

  const result: {
    query: string;
    topology: DebateTopology;
    startedAt: number | null;
    verdict: Verdict;
    personas: Persona[];
    turns: Turn[];
    consensusScore?: number;
    metrics?: DebateAggregateMetrics;
    sources?: FlatResearchSource[];
    ragCitations?: FlatRagCitation[];
  } = {
    query: core.query,
    topology: core.topology,
    startedAt: core.startedAt,
    verdict,
    personas,
    turns,
    metrics,
    sources: flatSources,
    ragCitations: flatRag,
  };
  if (core.consensusScore !== undefined) {
    result.consensusScore = core.consensusScore;
  }
  return result;
}

function replayTurnCharCount(t: ReplayTurn): number {
  if (typeof t.char_count === "number" && t.char_count > 0) return t.char_count;
  return t.final_text?.trim().length ?? 0;
}

function buildReplayMetrics(
  data: DebateReplayData,
  personas: Persona[],
  sourceCount: number,
  ragCitationCount: number,
): DebateAggregateMetrics {
  const personaById = new Map(personas.map((p) => [p.id, p]));
  const turnsBySpeaker = new Map<string, number>();
  const charsBySpeaker = new Map<string, number>();
  let lastEndedAt: number | null = null;
  let maxRound = -1;

  for (const turn of data.turns) {
    if (turn.round_index > maxRound) maxRound = turn.round_index;
    if (turn.ended_at) {
      const endedAt = new Date(turn.ended_at).getTime();
      if (Number.isFinite(endedAt) && (lastEndedAt === null || endedAt > lastEndedAt)) {
        lastEndedAt = endedAt;
      }
    }
    const chars = replayTurnCharCount(turn);
    if (chars <= 0) continue;
    charsBySpeaker.set(turn.speaker_id, (charsBySpeaker.get(turn.speaker_id) ?? 0) + chars);
    turnsBySpeaker.set(turn.speaker_id, (turnsBySpeaker.get(turn.speaker_id) ?? 0) + 1);
  }

  for (const stat of data.persona_stats ?? []) {
    if (stat.total_chars > 0) {
      charsBySpeaker.set(stat.persona_id, Math.max(charsBySpeaker.get(stat.persona_id) ?? 0, stat.total_chars));
    }
    if (stat.turn_count > 0) {
      turnsBySpeaker.set(stat.persona_id, Math.max(turnsBySpeaker.get(stat.persona_id) ?? 0, stat.turn_count));
    }
  }

  const totalCharacters = Array.from(charsBySpeaker.values()).reduce((sum, n) => sum + n, 0);
  const totalTurns = Array.from(turnsBySpeaker.values()).reduce((sum, n) => sum + n, 0);
  const bySpeaker: SpeakerStat[] = Array.from(charsBySpeaker.entries())
    .map(([speakerId, characters]) => {
      const persona = personaById.get(speakerId);
      return {
        speakerId,
        displayName: persona?.displayName ?? speakerId.slice(0, 8),
        expertise: persona?.expertise ?? null,
        themeColor: persona?.themeColor ?? null,
        turns: turnsBySpeaker.get(speakerId) ?? 0,
        characters,
        share: totalCharacters > 0 ? characters / totalCharacters : 0,
      };
    })
    .sort((a, b) => b.characters - a.characters);

  const startedAt = data.session.started_at ? new Date(data.session.started_at).getTime() : null;
  const durationSeconds =
    startedAt && lastEndedAt
      ? Math.max(0, Math.round((lastEndedAt - startedAt) / 1000))
      : null;

  return {
    totalTurns,
    totalRounds: maxRound + 1,
    durationSeconds,
    totalCharacters,
    totalSources: sourceCount,
    totalRagCitations: ragCitationCount,
    bySpeaker,
  };
}

/* ════════════════════════════════════════════════════════════════════
   Page Shell
   ════════════════════════════════════════════════════════════════════ */

export function ReplayPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [data, setData] = useState<DebateReplayData | null>(null);
  const [resilience, setResilience] = useState<V2ResilienceSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("summary");
  const loadVersionTree = useDebateStore((s) => s.loadVersionTree);
  const hydratePersonas = useDebateStore((s) => s.hydratePersonas);
  const applyBrainSnapshot = useDebateStore((s) => s.applyBrainSnapshot);
  const resetBrain = useDebateStore((s) => s.resetBrain);

  useEffect(() => {
    if (!sessionId) {
      setError("Geçersiz tartışma bağlantısı");
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      fetchDebateReplay(sessionId),
      fetchV2ResilienceSnapshot(sessionId).catch(() => null),
    ])
      .then(([replay, resilienceSnapshot]) => {
        setData(replay);
        setResilience(resilienceSnapshot);

        // Bilişsel iz — `BrainModal` yalnızca store'dan okur, bu sayfa
        // ise verisini yerel state'te tutuyordu; hidrasyon olmadan modal
        // her üye için boş açılıyordu. Modal başlığı `personas`'tan
        // geldiği için ikisi birlikte doldurulmalı.
        resetBrain();
        const core = adaptReplayCore(replay);
        hydratePersonas({
          personas: core.personas,
          seatOrder: core.personas.map((p) => p.id),
        });
        for (const list of Object.values(replay.brain_snapshots ?? {})) {
          for (const snap of list) applyBrainSnapshot(snap);
        }
      })
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Yükleme hatası"),
      )
      .finally(() => setLoading(false));
  }, [sessionId, hydratePersonas, applyBrainSnapshot, resetBrain]);

  // Bir sonraki oturumun profilini bu oturumun izleriyle karıştırma.
  useEffect(() => () => resetBrain(), [resetBrain]);

  // Lazy-load the version lineage on demand. We only fetch once the
  // operator opens the Versiyonlar tab so we don't pay the round-trip
  // for sessions where the tab is never visited. The slice itself is
  // idempotent — re-issuing the same load is a no-op once cached.
  useEffect(() => {
    if (activeTab === "versions" && sessionId) {
      void loadVersionTree(sessionId);
    }
  }, [activeTab, sessionId, loadVersionTree]);

  const personaMap = useMemo(() => {
    if (!data) return new Map<string, ReplayPersona>();
    return new Map(data.personas.map((p) => [p.id, p]));
  }, [data]);

  /* ── loading / error gates ───────────────────────────────────── */

  if (loading) {
    return (
      <AppShell>
        <main className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 space-y-6">
          <div className="h-32 rounded-2xl bg-white/[0.03] animate-pulse" />
          <div className="h-12 rounded-xl bg-white/[0.03] animate-pulse" />
          <div className="h-96 rounded-2xl bg-white/[0.03] animate-pulse" />
        </main>
      </AppShell>
    );
  }

  if (error || !data) {
    return (
      <AppShell>
        <main className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/20 mb-5">
            <svg
              className="w-6 h-6 text-rose-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            Rapor yüklenemedi
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {error ?? "Tartışma bulunamadı"}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 rounded-xl transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Tartışma Listesine Dön
          </Link>
        </main>
      </AppShell>
    );
  }

  /* ── derived KPIs ────────────────────────────────────────────── */

  const totalChars = data.persona_stats.reduce(
    (s, p) => s + p.total_chars,
    0,
  );
  const totalTokens = data.persona_stats.reduce(
    (s, p) => s + p.total_tokens,
    0,
  );
  const totalCritiques = data.persona_stats.reduce(
    (s, p) => s + p.critique_count,
    0,
  );
  const tone = consensusTone(data.session.consensus_score);

  /* ── JSON export ─────────────────────────────────────────────── */

  function downloadJson() {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `debate-${data.session.id}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /* ── PDF export ──────────────────────────────────────────────── */
  function openPdfReport() {
    if (!data || !data.verdict) return;
    const adapted = adaptReplayToVerdictReport(data);
    openVerdictReport(adapted);
  }

  return (
    <AppShell>
      <main className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 space-y-6">
        {/* ── Hero header ── */}
        <ReplayHero
          data={data}
          tone={tone}
          totalChars={totalChars}
          totalTokens={totalTokens}
          totalCritiques={totalCritiques}
          onExport={downloadJson}
          onPrintPdf={openPdfReport}
          canPrintPdf={Boolean(data.verdict)}
        />

        {/* ── Tab nav ── */}
        <div className="flex flex-wrap items-center gap-1 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-1.5">
          {TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={[
                  "inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-[12.5px] font-semibold transition-all",
                  active
                    ? "bg-gradient-to-br from-indigo-500/20 to-violet-500/15 text-white border border-indigo-500/30 shadow-inner"
                    : "text-slate-400 hover:text-white hover:bg-white/[0.04] border border-transparent",
                ].join(" ")}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.7}
                    d={tab.icon}
                  />
                </svg>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Tab body ── */}
        {resilience && <ResilienceSnapshotBanner snapshot={resilience} />}
        {activeTab === "summary" && (
          <SummaryTab data={data} personaMap={personaMap} tone={tone} />
        )}
        {activeTab === "ask" && (
          <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
            <Suspense
              fallback={
                <p className="text-sm text-slate-500">Yükleniyor…</p>
              }
            >
              <CouncilQaTab sessionId={sessionId ?? null} ready />
            </Suspense>
          </section>
        )}
        {activeTab === "commentary" && (
          <CommentaryTab data={data} onUpdated={setData} />
        )}
        {activeTab === "transcript" && (
          <TranscriptTab turns={data.turns} personaMap={personaMap} />
        )}
        {activeTab === "trajectory" && (
          <TrajectoryTab trajectory={data.consensus_trajectory} />
        )}
        {activeTab === "plan" && <ActionPlanTab data={data} />}
        {activeTab === "sources" && <SourcesTab data={data} />}
        {activeTab === "personas" && (
          <PersonasTab
            personas={data.personas}
            stats={data.persona_stats}
            totalCritiques={totalCritiques}
          />
        )}
        {activeTab === "simulation" && <SimulationTab data={data} />}
        {activeTab === "versions" && sessionId && (
          <VersionsTab debateId={sessionId} />
        )}
      </main>

      {/* Bilişsel Profil — müzakere ve katılımcı sekmelerindeki üye
          adlarından açılır. Kendi görünürlüğünü store'daki
          `selectedBrainPersonaId` üzerinden yönetir. */}
      <Suspense fallback={null}>
        <BrainModal />
      </Suspense>
    </AppShell>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Hero
   ════════════════════════════════════════════════════════════════════ */

function ReplayHero({
  data,
  tone,
  totalChars,
  totalTokens,
  totalCritiques,
  onExport,
  onPrintPdf,
  canPrintPdf,
}: {
  data: DebateReplayData;
  tone: ReturnType<typeof consensusTone>;
  totalChars: number;
  totalTokens: number;
  totalCritiques: number;
  onExport: () => void;
  onPrintPdf: () => void;
  canPrintPdf: boolean;
}) {
  // Rerun modal — local state because the modal is fully self-contained
  // and only this hero surfaces the trigger. Lifting the state would
  // bloat the page-level container without any shared-consumer benefit.
  const [rerunOpen, setRerunOpen] = useState(false);
  // Only finalized debates are rerunnable. Spawning a new version off
  // a still-running or cancelled debate would inherit half-formed
  // personas and broken seat orders. Server-side `/rerun` would also
  // happily accept such a fork but the result would be confusing.
  const canRerun = data.session.status === "finalized";

  const created = new Date(data.session.created_at);
  const dateStr = created.toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timeStr = created.toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] via-white/[0.02] to-transparent p-6 lg:p-8">
      {/* Ambient grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative flex items-start justify-between gap-6 mb-5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            <Link
              to="/"
              className="hover:text-indigo-300 transition-colors flex items-center gap-1"
            >
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Konsey Raporu
            </Link>
            <span className="text-slate-700">·</span>
            <span>
              {dateStr} · {timeStr}
            </span>
          </div>
          <h1 className="mt-3 text-2xl lg:text-3xl font-bold tracking-tight text-white leading-tight">
            {data.session.query}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <SessionBadge label={data.session.topology} variant="indigo" />
            <SessionBadge
              label={
                data.session.status === "finalized"
                  ? "TAMAMLANDI"
                  : data.session.status.toUpperCase()
              }
              variant={
                data.session.status === "finalized" ? "emerald" : "amber"
              }
            />
            <SessionBadge label={tone.label} variant="cyan" />
          </div>
        </div>

        <div className="shrink-0 flex flex-col items-end gap-2">
          {canRerun && (
            <button
              onClick={() => setRerunOpen(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-[12px] font-semibold text-white bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 hover:from-violet-500 hover:via-fuchsia-500 hover:to-pink-500 rounded-xl shadow-lg shadow-violet-500/30 ring-1 ring-violet-400/40 transition-all"
              title="Aynı soruyu farklı persona/tur/model ile yeniden çalıştır"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.7}
                  d="M6 3v12m0 0a3 3 0 100 6 3 3 0 000-6zm0-12a3 3 0 110 6 3 3 0 010-6zm12 0a3 3 0 110 6 3 3 0 010-6zM18 9a9 9 0 01-9 9"
                />
              </svg>
              Yeni Versiyonu Çalıştır
            </button>
          )}
          {canPrintPdf && (
            <button
              onClick={onPrintPdf}
              className="inline-flex items-center gap-2 px-3.5 py-2 text-[12px] font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-xl shadow-lg shadow-emerald-500/20 transition-all"
              title="Markalı PDF raporunu yeni sekmede aç"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.7}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              PDF Rapor
            </button>
          )}
          <button
            onClick={onExport}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-[12px] font-semibold text-slate-300 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded-xl transition-all"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.7}
                d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
              />
            </svg>
            JSON İndir
          </button>
          <Link
            to="/debate"
            className="inline-flex items-center gap-2 px-3.5 py-2 text-[12px] font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 rounded-xl shadow-lg shadow-indigo-500/20 transition-all"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Yeni Tartışma
          </Link>
        </div>
      </div>

      {/* KPI strip */}
      <div className="relative grid grid-cols-2 lg:grid-cols-6 gap-3">
        <KpiTile
          label="Konsensüs"
          value={
            data.session.consensus_score != null
              ? `%${data.session.consensus_score.toFixed(0)}`
              : "—"
          }
          tone={tone.text}
          ring={tone.ring}
          accent
        />
        <KpiTile
          label="Süre"
          value={formatDuration(data.session.duration_seconds)}
          tone="text-slate-100"
        />
        <KpiTile
          label="Tur"
          value={String(data.session.turn_count ?? data.turns.length)}
          tone="text-slate-100"
        />
        <KpiTile
          label="Katılımcı"
          value={String(data.personas.length)}
          tone="text-slate-100"
        />
        <KpiTile
          label="Kritik"
          value={String(totalCritiques)}
          tone="text-amber-300"
        />
        <KpiTile
          label="Token"
          value={
            totalTokens > 0 ? `~${(totalTokens / 1000).toFixed(1)}K` : "—"
          }
          {...(totalChars > 0
            ? { sublabel: `${formatNumber(totalChars)} char` }
            : {})}
          tone="text-violet-300"
        />
      </div>

      {/* Rerun modal — opens from the "Yeni Versiyonu Çalıştır" button
          in the hero header. Mounted unconditionally so React preserves
          its internal state (form values) across re-renders triggered
          by the parent's tab navigation. */}
      <RerunVersionModal
        open={rerunOpen}
        parentDebateId={data.session.id}
        parent={{
          query: data.session.query,
          // Prefer the explicit configuration the parent debate was
          // launched with. Fall back to derived values only when the
          // backend payload predates the schema extension.
          agentCount:
            data.session.agent_count ?? data.personas.length ?? 5,
          maxRounds:
            data.session.max_rounds ??
            (data.session.turn_count && data.personas.length
              ? Math.max(
                  2,
                  Math.ceil(data.session.turn_count / Math.max(1, data.personas.length)),
                )
              : 5),
          topology: data.session.topology,
        }}
        onClose={() => setRerunOpen(false)}
      />
    </div>
  );
}

function SessionBadge({
  label,
  variant,
}: {
  label: string;
  variant: "indigo" | "emerald" | "amber" | "cyan";
}) {
  const styles: Record<typeof variant, string> = {
    indigo:
      "bg-indigo-500/10 text-indigo-300 border-indigo-500/25",
    emerald:
      "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/25",
    cyan: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25",
  };
  return (
    <span
      className={[
        "inline-flex items-center px-2.5 py-1 rounded-md text-[10.5px] font-semibold uppercase tracking-wider border",
        styles[variant],
      ].join(" ")}
    >
      {label}
    </span>
  );
}

function KpiTile({
  label,
  value,
  sublabel,
  tone,
  ring,
  accent,
}: {
  label: string;
  value: string;
  sublabel?: string;
  tone: string;
  ring?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={[
        "rounded-xl border bg-white/[0.02] px-4 py-3",
        accent
          ? `border-white/[0.08] ring-1 ${ring ?? ""}`
          : "border-white/[0.05]",
      ].join(" ")}
    >
      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
        {label}
      </p>
      <p className={`mt-1 text-xl font-bold tabular-nums ${tone}`}>{value}</p>
      {sublabel && (
        <p className="text-[10px] text-slate-600 truncate mt-0.5">{sublabel}</p>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Summary Tab
   ════════════════════════════════════════════════════════════════════ */

function SummaryTab({
  data,
  personaMap,
  tone,
}: {
  data: DebateReplayData;
  personaMap: Map<string, ReplayPersona>;
  tone: ReturnType<typeof consensusTone>;
}) {
  const verdict = data.verdict;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Executive summary spans 2 cols */}
      <section className="lg:col-span-2 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
        <SectionHead
          icon="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"
          title="Yönetici Özeti"
          subtitle="Konseyin nihai mutabakat tezi"
          rightLabel={tone.label}
          rightTone={tone.text}
          rightBg={tone.bg}
        />
        {verdict ? (
          <p className="text-[14px] text-slate-200 leading-relaxed whitespace-pre-wrap">
            {verdict.executive}
          </p>
        ) : (
          <EmptyHint>Bu oturum için henüz konsey kararı oluşturulmamış.</EmptyHint>
        )}
      </section>

      {/* Dissent panel */}
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
        <SectionHead
          icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          title="Muhalefet Şerhi"
          subtitle={`${verdict?.dissenting?.length ?? 0} not`}
        />
        {verdict?.dissenting && verdict.dissenting.length > 0 ? (
          <ul className="space-y-3">
            {verdict.dissenting.map((d, i) => (
              <DissentItem
                key={i}
                dissent={d}
                personaMap={personaMap}
              />
            ))}
          </ul>
        ) : (
          <EmptyHint>Hiç muhalefet şerhi düşülmedi — tam mutabakat.</EmptyHint>
        )}
      </section>

      {/* Round overview spans full width below */}
      <section className="lg:col-span-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
        <SectionHead
          icon="M3 17l6-6 4 4 8-8M14 7h7v7"
          title="Müzakere Akışı"
          subtitle="Tur başına konuşma sayısı"
        />
        <RoundFlow turns={data.turns} personaMap={personaMap} />
      </section>
    </div>
  );
}

function SectionHead({
  icon,
  title,
  subtitle,
  rightLabel,
  rightTone,
  rightBg,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  rightLabel?: string;
  rightTone?: string;
  rightBg?: string;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-300">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.7}
              d={icon}
            />
          </svg>
        </div>
        <div>
          <h3 className="text-[14px] font-semibold text-white">{title}</h3>
          {subtitle && (
            <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      {rightLabel && (
        <span
          className={[
            "inline-flex items-center px-2.5 py-1 rounded-md text-[10.5px] font-semibold uppercase tracking-wider",
            rightBg ?? "",
            rightTone ?? "",
          ].join(" ")}
        >
          {rightLabel}
        </span>
      )}
    </div>
  );
}

function DissentItem({
  dissent,
  personaMap,
}: {
  dissent: ReplayDissent;
  personaMap: Map<string, ReplayPersona>;
}) {
  const persona = dissent.agentId
    ? personaMap.get(String(dissent.agentId))
    : undefined;
  const sev = severityChip(dissent.severity);
  return (
    <li className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3">
      <div className="flex items-center gap-2 mb-1.5">
        {persona && (
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: persona.theme_color ?? "#6366f1" }}
          />
        )}
        <span className="text-[12px] font-semibold text-slate-200 truncate">
          {persona?.display_name ?? "Bilinmeyen üye"}
        </span>
        <span
          className={[
            "ml-auto text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border",
            sev.className,
          ].join(" ")}
        >
          {sev.label}
        </span>
      </div>
      <p className="text-[12px] text-slate-400 leading-relaxed">
        {String(dissent.concern ?? "")}
      </p>
    </li>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-white/[0.08] bg-white/[0.01] px-4 py-6 text-center text-[12px] text-slate-500">
      {children}
    </div>
  );
}

/* ── Round flow strip — bar chart of turns per round ── */

function RoundFlow({
  turns,
  personaMap,
}: {
  turns: ReplayTurn[];
  personaMap: Map<string, ReplayPersona>;
}) {
  const byRound = new Map<number, ReplayTurn[]>();
  for (const t of turns) {
    const arr = byRound.get(t.round_index) ?? [];
    arr.push(t);
    byRound.set(t.round_index, arr);
  }
  const rounds = Array.from(byRound.entries()).sort((a, b) => a[0] - b[0]);
  if (rounds.length === 0) {
    return <EmptyHint>Henüz tur kaydı yok.</EmptyHint>;
  }
  const maxTurns = Math.max(...rounds.map(([, arr]) => arr.length), 1);

  return (
    <div className="flex items-end gap-3 overflow-x-auto pb-2">
      {rounds.map(([roundIdx, arr]) => {
        const heightPct = (arr.length / maxTurns) * 100;
        return (
          <div
            key={roundIdx}
            className="flex flex-col items-center gap-2 shrink-0"
            style={{ minWidth: 64 }}
          >
            <div
              className="relative w-12 rounded-lg overflow-hidden bg-white/[0.03] border border-white/[0.06]"
              style={{ height: 110 }}
            >
              <div
                className="absolute bottom-0 left-0 right-0 flex flex-col-reverse"
                style={{ height: `${Math.max(heightPct, 8)}%` }}
              >
                {arr.map((t) => {
                  const p = personaMap.get(t.speaker_id);
                  return (
                    <div
                      key={t.id}
                      className="flex-1 border-t border-slate-950/40"
                      style={{
                        backgroundColor: p?.theme_color ?? "#475569",
                        opacity: 0.85,
                      }}
                      title={`${p?.display_name ?? "?"} · ${
                        t.char_count ?? 0
                      } char`}
                    />
                  );
                })}
              </div>
            </div>
            <div className="text-[11px] text-slate-400 font-mono">
              T{roundIdx + 1}
            </div>
            <div className="text-[10px] text-slate-600">
              {arr.length} söz
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Transcript Tab — round-grouped bubbles
   ════════════════════════════════════════════════════════════════════ */

function TranscriptTab({
  turns,
  personaMap,
}: {
  turns: ReplayTurn[];
  personaMap: Map<string, ReplayPersona>;
}) {
  const byRound = useMemo(() => {
    const m = new Map<number, ReplayTurn[]>();
    for (const t of turns) {
      const arr = m.get(t.round_index) ?? [];
      arr.push(t);
      m.set(t.round_index, arr);
    }
    return Array.from(m.entries()).sort((a, b) => a[0] - b[0]);
  }, [turns]);

  if (byRound.length === 0) {
    return (
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-12 text-center">
        <EmptyHint>Bu oturumda hiç tur kaydı bulunmuyor.</EmptyHint>
      </section>
    );
  }

  return (
    <div className="space-y-6">
      {byRound.map(([roundIdx, arr]) => (
        <section
          key={roundIdx}
          className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden"
        >
          <div className="px-5 py-3 border-b border-white/[0.05] bg-white/[0.02] flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-indigo-500/15 border border-indigo-500/30 text-indigo-200 text-[11px] font-bold tabular-nums">
                {roundIdx + 1}
              </span>
              <span className="text-[13px] font-semibold text-slate-200">
                Tur {roundIdx + 1}
              </span>
            </div>
            <span className="text-[11px] text-slate-500 font-mono">
              {arr.length} konuşma
            </span>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {arr.map((turn) => {
              const persona = personaMap.get(turn.speaker_id);
              return (
                <TurnBubble
                  key={turn.id}
                  turn={turn}
                  persona={persona}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function TurnBubble({
  turn,
  persona,
}: {
  turn: ReplayTurn;
  persona: ReplayPersona | undefined;
}) {
  const critique = turn.critique;
  const sev = critique?.severity ? severityChip(critique.severity) : null;
  const openBrainModal = useDebateStore((s) => s.openBrainModal);
  // Beyin motoru kapalıyken koşmuş oturumlarda iz yok; o zaman adı
  // düğmeye çevirip boş bir modal vaat etmemek gerekir.
  const hasBrainTrace = useDebateStore((s) =>
    persona ? Boolean(s.brainSnapshots[persona.id]) : false,
  );
  return (
    <div className="px-5 py-4 hover:bg-white/[0.02] transition-colors">
      <div className="flex items-start gap-4">
        <div
          className="w-1 self-stretch rounded-full shrink-0 min-h-[40px]"
          style={{ backgroundColor: persona?.theme_color ?? "#475569" }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {hasBrainTrace && persona ? (
              <button
                type="button"
                onClick={() => openBrainModal(persona.id)}
                title="Bilişsel profili aç"
                className="text-[13.5px] font-semibold text-white underline decoration-dotted decoration-indigo-400/50 underline-offset-4 hover:decoration-indigo-300 transition-colors"
              >
                {persona.display_name}
              </button>
            ) : (
              <span className="text-[13.5px] font-semibold text-white">
                {persona?.display_name ?? "Bilinmeyen üye"}
              </span>
            )}
            {persona?.role && (
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                {persona.role}
              </span>
            )}
            <span className="ml-auto flex items-center gap-2 text-[10.5px] text-slate-500 font-mono">
              {turn.char_count != null && <span>{turn.char_count} char</span>}
              {turn.token_count != null && (
                <>
                  <span className="w-0.5 h-0.5 rounded-full bg-slate-700" />
                  <span>{turn.token_count} tok</span>
                </>
              )}
              {turn.duration_ms != null && turn.duration_ms > 0 && (
                <>
                  <span className="w-0.5 h-0.5 rounded-full bg-slate-700" />
                  <span>{(turn.duration_ms / 1000).toFixed(1)}s</span>
                </>
              )}
            </span>
          </div>

          {critique && critique.flaw && (
            <div className="mb-2 rounded-lg border border-amber-500/15 bg-amber-500/[0.05] px-3 py-2">
              <div className="flex items-start gap-2">
                {sev && (
                  <span
                    className={[
                      "shrink-0 text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border",
                      sev.className,
                    ].join(" ")}
                  >
                    {sev.label}
                  </span>
                )}
                <p className="text-[11.5px] text-amber-200/90 leading-relaxed italic">
                  {String(critique.flaw)}
                </p>
              </div>
            </div>
          )}

          <p className="text-[13.5px] text-slate-200 leading-relaxed whitespace-pre-wrap">
            {turn.final_text || (
              <span className="text-slate-600 italic">(Konuşmacı sözünü almadı)</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Trajectory Tab — consensus over rounds
   ════════════════════════════════════════════════════════════════════ */

function TrajectoryTab({
  trajectory,
}: {
  trajectory: ConsensusTrajectoryPoint[];
}) {
  if (trajectory.length === 0) {
    return (
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-12 text-center">
        <EmptyHint>Bu oturum için konsensüs trajektorisi kaydedilmedi.</EmptyHint>
      </section>
    );
  }

  const finalScore = trajectory[trajectory.length - 1]!.score;
  const peak = Math.max(...trajectory.map((p) => p.score));
  const trough = Math.min(...trajectory.map((p) => p.score));
  const deltaFromStart = finalScore - trajectory[0]!.score;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <TrajKpi
          label="Nihai"
          value={`%${finalScore.toFixed(1)}`}
          tone="text-emerald-300"
        />
        <TrajKpi
          label="Zirve"
          value={`%${peak.toFixed(1)}`}
          tone="text-cyan-300"
        />
        <TrajKpi
          label="Dip"
          value={`%${trough.toFixed(1)}`}
          tone="text-rose-300"
        />
        <TrajKpi
          label="Net Değişim"
          value={`${deltaFromStart >= 0 ? "+" : ""}${deltaFromStart.toFixed(1)} pp`}
          tone={
            deltaFromStart > 0
              ? "text-emerald-300"
              : deltaFromStart < 0
              ? "text-rose-300"
              : "text-slate-300"
          }
        />
      </div>

      {/* Chart */}
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
        <SectionHead
          icon="M3 17l6-6 4 4 8-8M14 7h7v7"
          title="Konsensüs Trajektorisi"
          subtitle="Tur bazında mutabakat skoru ilerlemesi"
        />
        <ConsensusChart trajectory={trajectory} />
      </section>

      {/* Table */}
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/[0.05] flex items-center justify-between">
          <h3 className="text-[13px] font-semibold text-slate-200">
            Tur Detayları
          </h3>
          <span className="text-[11px] text-slate-500 font-mono">
            {trajectory.length} satır
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-5 py-2.5 font-semibold">Tur</th>
                <th className="text-right px-5 py-2.5 font-semibold">Skor</th>
                <th className="text-right px-5 py-2.5 font-semibold">Durum</th>
                <th className="text-right px-5 py-2.5 font-semibold">Zaman</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {trajectory.map((p, i) => {
                const prev = i > 0 ? trajectory[i - 1]!.score : p.score;
                const delta = p.score - prev;
                const t = consensusTone(p.score);
                return (
                  <tr key={p.round} className="hover:bg-white/[0.02]">
                    <td className="px-5 py-2.5 font-mono text-slate-300">
                      T{p.round}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <span className={`font-bold tabular-nums ${t.text}`}>
                        %{p.score.toFixed(1)}
                      </span>
                      {i > 0 && (
                        <span
                          className={[
                            "ml-2 text-[10.5px] tabular-nums",
                            delta > 0
                              ? "text-emerald-400"
                              : delta < 0
                              ? "text-rose-400"
                              : "text-slate-500",
                          ].join(" ")}
                        >
                          {delta >= 0 ? "+" : ""}
                          {delta.toFixed(1)}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      {p.deadlock ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-500/10 text-rose-300 border border-rose-500/25">
                          KİLİT
                        </span>
                      ) : (
                        <span className="text-slate-500 text-[11px]">akış</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-right text-slate-500 font-mono text-[11px]">
                      {new Date(p.timestamp).toLocaleTimeString("tr-TR", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function TrajKpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
        {label}
      </p>
      <p className={`mt-1 text-xl font-bold tabular-nums ${tone}`}>{value}</p>
    </div>
  );
}

function ConsensusChart({
  trajectory,
}: {
  trajectory: ConsensusTrajectoryPoint[];
}) {
  const W = 720;
  const H = 200;
  const PAD_L = 36;
  const PAD_R = 16;
  const PAD_T = 16;
  const PAD_B = 28;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const stepX =
    trajectory.length > 1 ? innerW / (trajectory.length - 1) : innerW;

  const points = trajectory.map((p, i) => {
    const x = PAD_L + i * stepX;
    const y = PAD_T + innerH - (p.score / 100) * innerH;
    return [x, y, p] as const;
  });

  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(2)} ${p[1].toFixed(2)}`)
    .join(" ");

  const last = points[points.length - 1]!;
  const areaD =
    `${pathD} L ${last[0].toFixed(2)} ${PAD_T + innerH} ` +
    `L ${points[0]![0].toFixed(2)} ${PAD_T + innerH} Z`;

  // 3 horizontal grid lines @ 25, 50, 75
  const gridY = [25, 50, 75].map((v) => ({
    y: PAD_T + innerH - (v / 100) * innerH,
    label: `${v}%`,
  }));

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        style={{ minWidth: 520 }}
      >
        <defs>
          <linearGradient id="trajFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgb(34 211 238)" stopOpacity={0.35} />
            <stop offset="100%" stopColor="rgb(34 211 238)" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="trajStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgb(99 102 241)" />
            <stop offset="100%" stopColor="rgb(34 211 238)" />
          </linearGradient>
        </defs>

        {/* grid */}
        {gridY.map((g, i) => (
          <g key={i}>
            <line
              x1={PAD_L}
              x2={W - PAD_R}
              y1={g.y}
              y2={g.y}
              stroke="rgba(255,255,255,0.05)"
              strokeDasharray="2 4"
            />
            <text
              x={PAD_L - 6}
              y={g.y + 3}
              textAnchor="end"
              className="text-[9px]"
              fill="rgb(100 116 139)"
            >
              {g.label}
            </text>
          </g>
        ))}

        {/* area */}
        <path d={areaD} fill="url(#trajFill)" />
        {/* line */}
        <path
          d={pathD}
          fill="none"
          stroke="url(#trajStroke)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* points */}
        {points.map(([x, y, p]) => (
          <g key={p.round}>
            <circle
              cx={x}
              cy={y}
              r={p.deadlock ? 5 : 3.5}
              fill={p.deadlock ? "rgb(244 63 94)" : "rgb(165 243 252)"}
              stroke="rgb(15 23 42)"
              strokeWidth={1.5}
            />
            {p.deadlock && (
              <circle
                cx={x}
                cy={y}
                r={9}
                fill="none"
                stroke="rgb(244 63 94)"
                strokeOpacity={0.4}
                strokeWidth={1}
              />
            )}
          </g>
        ))}

        {/* x-axis labels */}
        {points.map(([x, , p], i) => {
          // Only every Nth label if dense
          const skip = points.length > 12;
          if (skip && i % 2 !== 0 && i !== points.length - 1) return null;
          return (
            <text
              key={p.round}
              x={x}
              y={H - 8}
              textAnchor="middle"
              className="text-[9.5px] font-mono"
              fill="rgb(100 116 139)"
            >
              T{p.round}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Action Plan Tab — architectural / strategic / tactical
   ════════════════════════════════════════════════════════════════════ */

function ActionPlanTab({ data }: { data: DebateReplayData }) {
  const rawPlan = data.verdict?.action_plan;
  if (!rawPlan) {
    return (
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-12 text-center">
        <EmptyHint>Bu oturum için eylem planı oluşturulmamış.</EmptyHint>
      </section>
    );
  }
  const plan = coerceActionPlan(rawPlan);
  const totalActions =
    plan.immediate.length +
    plan.tactical.length +
    plan.strategic.length +
    plan.architectural.length +
    plan.governance.length;

  return (
    <div className="flex flex-col gap-5">
      {/* Framework header — McKinsey 3-Horizons + SMART + RACI */}
      <header className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-[12px] text-slate-400">
        <span className="font-semibold text-white">{totalActions} aksiyon</span>
        <span className="text-slate-600">·</span>
        <span>{plan.risks?.length ?? 0} risk</span>
        <span className="text-slate-600">·</span>
        <span>{plan.kpis?.length ?? 0} KPI</span>
        <span className="text-slate-600">·</span>
        <span>{plan.criticalAssumptions?.length ?? 0} varsayım</span>
        <span
          className="ml-auto rounded-full bg-violet-500/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-violet-200 ring-1 ring-violet-400/30"
          title="McKinsey 3-Horizons + SMART + RACI çerçevesi"
        >
          5-ufuk · SMART · RACI
        </span>
      </header>

      {/* 5-tier grid — 2 columns on lg, 3 on 2xl. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-5">
        <PlanColumn
          title="Acil"
          subtitle="0-2 hafta · acil müdahale"
          items={plan.immediate}
          accent="rose"
        />
        <PlanColumn
          title="Taktik"
          subtitle="2-12 hafta · sprint icrası"
          items={plan.tactical}
          accent="emerald"
        />
        <PlanColumn
          title="Stratejik"
          subtitle="3-12 ay · program kararları"
          items={plan.strategic}
          accent="cyan"
        />
        <PlanColumn
          title="Mimari"
          subtitle="12-36 ay · platform/mimari"
          items={plan.architectural}
          accent="indigo"
        />
        <PlanColumn
          title="Yönetişim"
          subtitle="Sürekli · karar hakları & RACI"
          items={plan.governance}
          accent="amber"
        />
      </div>

      {/* Plan-level annexes */}
      {(plan.risks?.length ?? 0) > 0 && (
        <ReplayRiskRegister risks={plan.risks ?? []} />
      )}
      {(plan.kpis?.length ?? 0) > 0 && (
        <ReplayKpiPanel kpis={plan.kpis ?? []} />
      )}
      {(plan.criticalAssumptions?.length ?? 0) > 0 && (
        <ReplayAssumptionsPanel assumptions={plan.criticalAssumptions ?? []} />
      )}
    </div>
  );
}

const PLAN_ACCENTS = {
  rose: {
    ring: "before:bg-rose-500/40",
    tile: "bg-rose-500/15 border-rose-500/30 text-rose-200",
  },
  emerald: {
    ring: "before:bg-emerald-500/40",
    tile: "bg-emerald-500/15 border-emerald-500/30 text-emerald-200",
  },
  cyan: {
    ring: "before:bg-cyan-500/40",
    tile: "bg-cyan-500/15 border-cyan-500/30 text-cyan-200",
  },
  indigo: {
    ring: "before:bg-indigo-500/40",
    tile: "bg-indigo-500/15 border-indigo-500/30 text-indigo-200",
  },
  amber: {
    ring: "before:bg-amber-500/40",
    tile: "bg-amber-500/15 border-amber-500/30 text-amber-200",
  },
} as const;

type PlanAccent = keyof typeof PLAN_ACCENTS;

function PlanColumn({
  title,
  subtitle,
  items,
  accent,
}: {
  title: string;
  subtitle: string;
  items: ActionItem[];
  accent: PlanAccent;
}) {
  const c = PLAN_ACCENTS[accent];
  return (
    <section
      className={[
        "relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5",
        "before:absolute before:left-0 before:top-0 before:h-full before:w-[2px]",
        c.ring,
      ].join(" ")}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px] font-bold text-white">{title}</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        <span
          className={[
            "shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-mono tabular-nums",
            c.tile,
          ].join(" ")}
        >
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <EmptyHint>Bu kategori için kalem üretilmedi.</EmptyHint>
      ) : (
        <ol className="space-y-2.5">
          {items.map((it, i) => (
            <li
              key={i}
              className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-3 hover:border-white/[0.1] transition-colors"
            >
              <div className="flex items-start gap-2.5">
                <span
                  className={[
                    "shrink-0 w-6 h-6 rounded-md border flex items-center justify-center text-[11px] font-bold tabular-nums",
                    c.tile,
                  ].join(" ")}
                >
                  {i + 1}
                </span>
                <p className="flex-1 text-[13px] text-slate-200 leading-relaxed font-medium">
                  {it.title}
                </p>
                <div className="shrink-0 flex items-center gap-1">
                  <span
                    className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-mono font-bold text-slate-200 ring-1 ring-white/10"
                    title={`Öncelik ${it.priority ?? "P1"}`}
                  >
                    {it.priority ?? "P1"}
                  </span>
                  <span
                    className="rounded bg-white/[0.04] px-1.5 py-0.5 text-[10px] font-mono text-slate-300"
                    title={`Efor ${it.effort ?? "M"}`}
                  >
                    {it.effort ?? "M"}
                  </span>
                </div>
              </div>
              {it.rationale && (
                <p className="mt-2 ml-9 text-[11.5px] leading-relaxed text-slate-400">
                  {it.rationale}
                </p>
              )}
              {(it.owner ||
                it.deadlineWindow ||
                (it.successMetric && it.successMetric !== "—")) && (
                <div className="mt-2 ml-9 flex flex-wrap items-center gap-1.5 text-[11px]">
                  {it.owner && (
                    <span className="rounded-md bg-white/[0.06] px-2 py-0.5 text-slate-200 ring-1 ring-white/10">
                      👤 {it.owner}
                      {it.accountable && it.accountable !== it.owner
                        ? ` / ${it.accountable}`
                        : ""}
                    </span>
                  )}
                  {it.deadlineWindow && (
                    <span className="rounded-md bg-amber-500/10 px-2 py-0.5 text-amber-100 ring-1 ring-amber-400/30">
                      ⏱️ {it.deadlineWindow}
                    </span>
                  )}
                  {it.successMetric && it.successMetric !== "—" && (
                    <span className="rounded-md bg-emerald-500/10 px-2 py-0.5 text-emerald-100 ring-1 ring-emerald-400/30">
                      🎯 {it.successMetric}
                    </span>
                  )}
                </div>
              )}
              {(it.dependencies?.length ?? 0) > 0 && (
                <div className="mt-1.5 ml-9 flex flex-wrap items-center gap-1 text-[10.5px] text-slate-400">
                  <span className="font-semibold uppercase tracking-wider">
                    bağımlılık:
                  </span>
                  {it.dependencies?.map((dep, di) => (
                    <span
                      key={di}
                      className="rounded bg-violet-500/10 px-1.5 py-0.5 text-violet-100 ring-1 ring-violet-400/30"
                    >
                      {dep}
                    </span>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function ReplayRiskRegister({ risks }: { risks: RiskEntry[] }) {
  return (
    <section className="rounded-2xl border border-rose-500/20 bg-rose-500/[0.04] p-5">
      <h3 className="text-[14px] font-bold text-white mb-3">
        Risk Register · {risks.length}
      </h3>
      <ol className="space-y-2.5">
        {risks.map((r, i) => (
          <li
            key={i}
            className="rounded-lg bg-white/[0.02] border border-white/[0.05] p-3"
          >
            <div className="flex items-start gap-2">
              <p className="flex-1 text-[13px] text-slate-200 font-medium leading-relaxed">
                {r.risk}
              </p>
              <div className="shrink-0 flex items-center gap-1 text-[10px] font-mono uppercase">
                <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-slate-200 ring-1 ring-white/10">
                  O:{(r.likelihood ?? "medium")[0]?.toUpperCase()}
                </span>
                <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-slate-200 ring-1 ring-white/10">
                  E:{(r.impact ?? "medium")[0]?.toUpperCase()}
                </span>
              </div>
            </div>
            <p className="mt-1.5 text-[11.5px] text-slate-400 leading-relaxed">
              <span className="font-semibold text-emerald-300">
                Mitigasyon:
              </span>{" "}
              {r.mitigation}
            </p>
            {r.owner && (
              <p className="mt-1 text-[10.5px] text-slate-500">
                Sahip: <span className="text-slate-300">{r.owner}</span>
              </p>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

function ReplayKpiPanel({ kpis }: { kpis: KpiEntry[] }) {
  return (
    <section className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5">
      <h3 className="text-[14px] font-bold text-white mb-3">
        Yürütme KPI'ları · {kpis.length}
      </h3>
      <ul className="space-y-2">
        {kpis.map((k, i) => (
          <li
            key={i}
            className="flex flex-wrap items-center gap-2 rounded-lg bg-white/[0.02] border border-white/[0.05] px-3 py-2"
          >
            <span className="font-medium text-slate-200 text-[13px]">
              {k.metric}
            </span>
            <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-[11px] font-mono text-emerald-100 ring-1 ring-emerald-400/30">
              hedef: {k.target}
            </span>
            {k.cadence && (
              <span className="text-[11px] text-slate-500">· {k.cadence}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ReplayAssumptionsPanel({
  assumptions,
}: {
  assumptions: string[];
}) {
  return (
    <section className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-5">
      <h3 className="text-[14px] font-bold text-white mb-3">
        Kritik Varsayımlar · {assumptions.length}
      </h3>
      <ul className="space-y-1.5">
        {assumptions.map((a, i) => (
          <li
            key={i}
            className="flex items-start gap-2 rounded bg-white/[0.02] px-3 py-2 text-[13px] text-slate-200 leading-relaxed"
          >
            <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-amber-400" />
            <span className="min-w-0 break-words">{a}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Sources Tab — bibliography of every external + internal evidence
   piece the council touched, deduplicated and ordered by impact.
   ════════════════════════════════════════════════════════════════════ */

function SourcesTab({ data }: { data: DebateReplayData }) {
  const sources = data.sources_rollup ?? [];
  const documents = data.documents_rollup ?? [];
  const turnsById = new Map(data.turns.map((t) => [t.id, t]));
  const personaById = new Map(data.personas.map((p) => [p.id, p]));

  const [view, setView] = useState<"web" | "docs">(() =>
    documents.length > 0 && sources.length === 0 ? "docs" : "web",
  );

  const totalSourceCitations = sources.reduce(
    (acc, s) => acc + s.cited_in_turns.length,
    0,
  );
  const totalDocCitations = documents.reduce(
    (acc, d) => acc + d.cited_in_turns.length,
    0,
  );

  const empty = sources.length === 0 && documents.length === 0;

  return (
    <div className="grid grid-cols-1 gap-6">
      {/* ── Hero / segmented control ── */}
      <div className="rounded-xl border border-line-soft/40 bg-surface-1 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="text-base font-bold text-fg-strong">
              Kaynaklar & Belgeler
            </h3>
            <p className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">
              Konseyin bu tartışmada gerçekten dayandığı{" "}
              <strong className="text-fg-strong">harici web kaynakları</strong>{" "}
              ve <strong className="text-fg-strong">yüklenmiş belge
              parçaları</strong>. Tüm öneriler bu kanıt zincirine bağlıdır;
              PDF raporun "Kaynaklar" bölümünde de aynısı yer alır.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1 rounded-lg border border-line-soft/40 bg-surface-0/60 p-1">
            <button
              type="button"
              onClick={() => setView("web")}
              className={[
                "px-3 py-1.5 rounded-md text-[12px] font-semibold transition",
                view === "web"
                  ? "bg-violet-500/15 text-violet-200 ring-1 ring-violet-500/30"
                  : "text-fg-muted hover:text-fg-strong",
              ].join(" ")}
              aria-pressed={view === "web"}
            >
              Web Kaynakları ({sources.length})
            </button>
            <button
              type="button"
              onClick={() => setView("docs")}
              className={[
                "px-3 py-1.5 rounded-md text-[12px] font-semibold transition",
                view === "docs"
                  ? "bg-violet-500/15 text-violet-200 ring-1 ring-violet-500/30"
                  : "text-fg-muted hover:text-fg-strong",
              ].join(" ")}
              aria-pressed={view === "docs"}
            >
              Yüklü Belgeler ({documents.length})
            </button>
          </div>
        </div>

        {/* ── KPI strip ── */}
        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SourceKpi
            label="Web kaynak"
            value={sources.length}
            tone="violet"
          />
          <SourceKpi
            label="Web atıf"
            value={totalSourceCitations}
            tone="cyan"
          />
          <SourceKpi
            label="Belge"
            value={documents.length}
            tone="amber"
          />
          <SourceKpi
            label="Belge atıf"
            value={totalDocCitations}
            tone="emerald"
          />
        </dl>
      </div>

      {/* ── Empty state ── */}
      {empty && (
        <div className="rounded-xl border border-line-soft/40 bg-surface-1 p-12 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-300 ring-1 ring-amber-500/20">
            <svg
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
          </div>
          <h4 className="text-sm font-bold text-fg-strong">
            Bu tartışma harici kanıt çekmedi
          </h4>
          <p className="mt-2 text-[12px] text-fg-muted max-w-md mx-auto">
            Konsey ne web araştırması yaptı ne de yüklenmiş bir belgeden
            alıntı yaptı. Bu, basit konularda (kısa, opinion-bazlı sorular)
            normaldir. Aksini bekliyorsan tartışmayı çatallayıp web
            araştırmasını teşvik eden bir başlangıç sorusu kullanabilirsin.
          </p>
        </div>
      )}

      {/* ── Web sources list ── */}
      {view === "web" && sources.length > 0 && (
        <ol className="grid grid-cols-1 gap-3">
          {sources.map((src, i) => (
            <li
              key={src.url}
              className="rounded-xl border border-line-soft/40 bg-surface-1 p-4 hover:border-violet-500/30 transition"
            >
              <div className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-violet-500/10 text-[11px] font-bold text-violet-300 ring-1 ring-violet-500/20">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[13.5px] font-semibold text-fg-strong hover:text-violet-300 hover:underline break-words"
                    >
                      {src.title || src.url}
                    </a>
                    <span className="text-[11px] text-fg-muted font-mono">
                      {src.name}
                    </span>
                    {src.published && (
                      <span className="text-[10.5px] text-fg-muted">
                        ·{" "}
                        {new Date(src.published).toLocaleDateString("tr-TR", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    )}
                  </div>
                  {src.summary && (
                    <p className="mt-1 text-[12px] leading-relaxed text-fg-muted line-clamp-3">
                      {src.summary}
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[10.5px] text-fg-muted">
                      Atıf yapan:
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {src.cited_in_turns.slice(0, 5).map((tid) => {
                        const turn = turnsById.get(tid);
                        if (!turn) return null;
                        const persona = personaById.get(turn.speaker_id);
                        return (
                          <span
                            key={tid}
                            className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/5 text-fg-muted ring-1 ring-white/10"
                            title={`Tur ${turn.round_index}`}
                          >
                            {persona?.display_name ?? "?"}
                          </span>
                        );
                      })}
                      {src.cited_in_turns.length > 5 && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/5 text-fg-muted ring-1 ring-white/10">
                          +{src.cited_in_turns.length - 5}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <a
                  href={src.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 rounded-md p-1.5 text-fg-muted hover:bg-white/5 hover:text-violet-300"
                  title="Dış bağlantıyı aç"
                  aria-label="Dış bağlantıyı aç"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* ── Documents list ── */}
      {view === "docs" && documents.length > 0 && (
        <div className="grid grid-cols-1 gap-3">
          {documents.map((doc) => (
            <details
              key={doc.doc_id}
              className="group rounded-xl border border-line-soft/40 bg-surface-1 overflow-hidden"
            >
              <summary className="cursor-pointer list-none px-4 py-3 hover:bg-white/[0.02]">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-300 ring-1 ring-amber-500/20">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-[13.5px] font-semibold text-fg-strong truncate">
                        {doc.filename}
                      </span>
                      <span
                        className={[
                          "px-1.5 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wide",
                          doc.scope === "pool"
                            ? "bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/30"
                            : "bg-fuchsia-500/15 text-fuchsia-300 ring-1 ring-fuchsia-500/30",
                        ].join(" ")}
                      >
                        {doc.scope === "pool" ? "Ortak" : "Özel"}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-fg-muted">
                      <span>
                        <strong className="text-fg-strong">
                          {doc.chunk_count}
                        </strong>{" "}
                        parça
                      </span>
                      <span>·</span>
                      <span>
                        Maks. benzerlik:{" "}
                        <strong className="text-emerald-300">
                          {(doc.max_score * 100).toFixed(0)}%
                        </strong>
                      </span>
                      <span>·</span>
                      <span>
                        <strong className="text-fg-strong">
                          {doc.cited_in_turns.length}
                        </strong>{" "}
                        tur
                      </span>
                    </div>
                  </div>
                  <svg
                    className="h-4 w-4 text-fg-muted shrink-0 transition group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </summary>
              <div className="border-t border-line-soft/40 px-4 py-3 bg-surface-0/40 grid grid-cols-1 gap-2">
                {doc.chunks.slice(0, 8).map((chunk, idx) => (
                  <blockquote
                    key={`${chunk.doc_id}-${chunk.chunk_idx}-${idx}`}
                    className="rounded-md border-l-2 border-amber-400/40 bg-amber-500/[0.04] px-3 py-2"
                  >
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="text-[10.5px] font-mono text-fg-muted">
                        parça #{chunk.chunk_idx}
                      </span>
                      <span className="text-[10.5px] text-emerald-300/80 font-semibold">
                        {(chunk.score * 100).toFixed(0)}%
                      </span>
                    </div>
                    <p className="text-[12.5px] leading-relaxed text-fg-strong/90 italic">
                      "{chunk.excerpt}"
                    </p>
                  </blockquote>
                ))}
                {doc.chunks.length > 8 && (
                  <p className="text-[11px] text-fg-muted text-center py-1">
                    +{doc.chunks.length - 8} parça daha PDF raporda
                    listelenmiştir.
                  </p>
                )}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function SourceKpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "violet" | "cyan" | "amber" | "emerald";
}) {
  const toneMap = {
    violet: "text-violet-300",
    cyan: "text-cyan-300",
    amber: "text-amber-300",
    emerald: "text-emerald-300",
  } as const;
  return (
    <div className="rounded-lg border border-line-soft/40 bg-surface-0/40 px-3 py-2">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-muted">
        {label}
      </dt>
      <dd className={`mt-1 text-xl font-bold tabular-nums ${toneMap[tone]}`}>
        {value.toLocaleString("tr-TR")}
      </dd>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Personas Tab — contribution scoreboard
   ════════════════════════════════════════════════════════════════════ */

function PersonasTab({
  personas,
  stats,
  totalCritiques,
}: {
  personas: ReplayPersona[];
  stats: PersonaContributionStats[];
  totalCritiques: number;
}) {
  const personaMap = new Map(personas.map((p) => [p.id, p]));
  const maxTurns = Math.max(...stats.map((s) => s.turn_count), 1);
  const maxChars = Math.max(...stats.map((s) => s.total_chars), 1);

  // Personas with no recorded stats (silent) get a placeholder row.
  const ranked = [
    ...stats.map((s) => ({ stat: s, persona: personaMap.get(s.persona_id) })),
    ...personas
      .filter((p) => !stats.some((s) => s.persona_id === p.id))
      .map((p) => ({
        stat: {
          persona_id: p.id,
          turn_count: 0,
          total_chars: 0,
          total_tokens: 0,
          avg_chars_per_turn: 0,
          critique_count: 0,
        },
        persona: p,
      })),
  ];

  return (
    <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      <div className="px-5 py-4 border-b border-white/[0.05] flex items-center justify-between">
        <SectionHead
          icon="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-2a4 4 0 100-8 4 4 0 000 8zm6 0a4 4 0 100-8 4 4 0 000 8z"
          title="Katılımcı Puan Tablosu"
          subtitle={`${personas.length} üye · ${totalCritiques} kritik`}
        />
      </div>
      <div className="divide-y divide-white/[0.04]">
        {ranked.map(({ stat, persona }, i) => (
          <PersonaRow
            key={stat.persona_id}
            rank={i + 1}
            persona={persona}
            stat={stat}
            maxTurns={maxTurns}
            maxChars={maxChars}
          />
        ))}
      </div>
    </section>
  );
}

function PersonaRow({
  rank,
  persona,
  stat,
  maxTurns,
  maxChars,
}: {
  rank: number;
  persona: ReplayPersona | undefined;
  stat: PersonaContributionStats;
  maxTurns: number;
  maxChars: number;
}) {
  const turnPct = (stat.turn_count / maxTurns) * 100;
  const charPct = (stat.total_chars / maxChars) * 100;
  const openBrainModal = useDebateStore((s) => s.openBrainModal);
  const hasBrainTrace = useDebateStore((s) =>
    persona ? Boolean(s.brainSnapshots[persona.id]) : false,
  );
  return (
    <div className="px-5 py-4 hover:bg-white/[0.02] transition-colors">
      <div className="flex items-center gap-4">
        <span className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-[11px] font-mono text-slate-500 shrink-0">
          #{rank}
        </span>

        <div
          className="w-2.5 h-9 rounded-full shrink-0"
          style={{ backgroundColor: persona?.theme_color ?? "#475569" }}
        />

        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[2fr_1.5fr_1.5fr] gap-3 items-center">
          <div className="min-w-0">
            {hasBrainTrace && persona ? (
              <button
                type="button"
                onClick={() => openBrainModal(persona.id)}
                title="Bilişsel profili aç"
                className="block max-w-full truncate text-left text-[13.5px] font-semibold text-white underline decoration-dotted decoration-indigo-400/50 underline-offset-4 hover:decoration-indigo-300 transition-colors"
              >
                {persona.display_name}
              </button>
            ) : (
              <p className="text-[13.5px] font-semibold text-white truncate">
                {persona?.display_name ?? "Bilinmeyen üye"}
              </p>
            )}
            <p className="text-[11px] text-slate-500 truncate">
              {persona?.expertise ?? persona?.role ?? "—"}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10.5px] text-slate-500 uppercase tracking-wider font-semibold">
                Söz
              </span>
              <span className="text-[11px] text-slate-300 font-mono tabular-nums">
                {stat.turn_count}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-violet-500"
                style={{ width: `${turnPct}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10.5px] text-slate-500 uppercase tracking-wider font-semibold">
                Hacim
              </span>
              <span className="text-[11px] text-slate-300 font-mono tabular-nums">
                {(stat.total_chars / 1000).toFixed(1)}k char
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500"
                style={{ width: `${charPct}%` }}
              />
            </div>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
              Kritik
            </p>
            <p className="text-[14px] font-bold text-amber-300 tabular-nums">
              {stat.critique_count}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
              Token
            </p>
            <p className="text-[14px] font-bold text-violet-300 tabular-nums">
              {stat.total_tokens > 0
                ? `${(stat.total_tokens / 1000).toFixed(1)}k`
                : "—"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Simulation Tab — chaos rollout outcomes
   ════════════════════════════════════════════════════════════════════ */

function SimulationTab({ data }: { data: DebateReplayData }) {
  const verdict = data.verdict;
  const outcomes = verdict?.simulation_outcomes ?? [];
  const score = verdict?.simulation_score;

  const survived = outcomes.filter((o) => o.survived === true).length;
  const failed = outcomes.filter((o) => o.survived === false).length;
  const total = outcomes.length;

  if (!verdict || (total === 0 && score == null)) {
    return (
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-12 text-center">
        <EmptyHint>
          Bu oturumda chaos-rollout simülasyonu çalıştırılmadı.
        </EmptyHint>
      </section>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SimKpi
          label="Simülasyon Skoru"
          value={score != null ? score.toFixed(2) : "—"}
          tone="text-violet-300"
          accent
        />
        <SimKpi
          label="Hayatta Kalan Senaryolar"
          value={total > 0 ? `${survived} / ${total}` : "—"}
          tone="text-emerald-300"
        />
        <SimKpi
          label="Kırılan Senaryolar"
          value={failed > 0 ? String(failed) : "0"}
          tone="text-rose-300"
        />
      </div>

      {total > 0 && (
        <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          <div className="px-5 py-3 border-b border-white/[0.05] flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-slate-200">
              Senaryo Analizi
            </h3>
            <span className="text-[11px] text-slate-500 font-mono">
              {total} senaryo
            </span>
          </div>
          <ul className="divide-y divide-white/[0.04]">
            {outcomes.map((o, i) => (
              <li key={i} className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <span
                    className={[
                      "shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center",
                      o.survived
                        ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                        : "bg-rose-500/15 text-rose-300 border border-rose-500/30",
                    ].join(" ")}
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      {o.survived ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      )}
                    </svg>
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-100">
                      {o.scenario ?? "Senaryo"}
                    </p>
                    {o.rationale && (
                      <p className="text-[12px] text-slate-400 leading-relaxed mt-1">
                        {o.rationale}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function SimKpi({
  label,
  value,
  tone,
  accent,
}: {
  label: string;
  value: string;
  tone: string;
  accent?: boolean;
}) {
  return (
    <div
      className={[
        "rounded-2xl border bg-white/[0.02] px-5 py-4",
        accent
          ? "border-violet-500/20 ring-1 ring-violet-500/20"
          : "border-white/[0.06]",
      ].join(" ")}
    >
      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
        {label}
      </p>
      <p className={`mt-1.5 text-3xl font-bold tabular-nums ${tone}`}>
        {value}
      </p>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Commentary Tab — editorial LLM perspective on the verdict
   ────────────────────────────────────────────────────────────────────
   • If the verdict already carries commentary (live debates after
     EPIC-3 rollout), render the two blocks immediately.
   • Otherwise expose an on-demand generation button hitting the
     `POST /api/debate/{id}/commentary` endpoint, then patch the
     parent state so the user can re-export the PDF with commentary
     included without reloading the page.
   ════════════════════════════════════════════════════════════════════ */

function CommentaryTab({
  data,
  onUpdated,
}: {
  data: DebateReplayData;
  onUpdated: (next: DebateReplayData) => void;
}) {
  const verdict = data.verdict;
  const initialAnalysis = verdict?.commentary_analysis ?? null;
  const initialCritique = verdict?.commentary_critique ?? null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(async () => {
    if (!data.session.id) return;
    setLoading(true);
    setError(null);
    try {
      const csrf = document.cookie
        .split("; ")
        .find((c) => c.startsWith("macdm_csrf="))
        ?.split("=")[1];
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (csrf) headers["X-Macdm-Csrf"] = csrf;
      const res = await fetch(
        `/api/debate/${data.session.id}/commentary`,
        {
          method: "POST",
          credentials: "include",
          headers,
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body: {
        commentaryAnalysis?: string | null;
        commentaryCritique?: string | null;
      } = await res.json();
      const a = body.commentaryAnalysis ?? null;
      const c = body.commentaryCritique ?? null;
      if (!a && !c) {
        setError("Yorum üretilemedi. Lütfen tekrar deneyin.");
        return;
      }
      if (data.verdict) {
        onUpdated({
          ...data,
          verdict: {
            ...data.verdict,
            commentary_analysis: a,
            commentary_critique: c,
          },
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bağlantı hatası");
    } finally {
      setLoading(false);
    }
  }, [data, onUpdated]);

  if (!verdict) {
    return (
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-12 text-center">
        <EmptyHint>
          Bu oturum için henüz konsey kararı oluşturulmadığından editoryal
          yorum üretilemiyor.
        </EmptyHint>
      </section>
    );
  }

  if (!initialAnalysis && !initialCritique) {
    return (
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10">
        <div className="flex flex-col items-center gap-5 text-center">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-300">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.7}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <div className="max-w-md">
            <h3 className="text-[15px] font-semibold text-white">
              Editoryal yorum henüz üretilmedi
            </h3>
            <p className="mt-2 text-[12.5px] text-slate-400 leading-relaxed">
              Bu tartışma, editoryal yorum özelliği eklenmeden önce
              tamamlanmış. Bağımsız bir LLM ile <strong>yapıcı analiz</strong>{" "}
              ve <strong>eleştirel perspektif</strong> içeren bir yorum
              üretmek için aşağıdaki butona tıklayın. İşlem birkaç saniye
              sürebilir.
            </p>
          </div>
          <button
            onClick={generate}
            disabled={loading}
            className={[
              "inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-[12.5px] font-semibold",
              "bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500",
              "text-white shadow-lg shadow-indigo-500/20 transition-all",
              "disabled:opacity-60 disabled:cursor-not-allowed",
            ].join(" ")}
          >
            {loading ? (
              <>
                <svg
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Yorum üretiliyor...
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.7}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
                Editoryal Yorum Üret
              </>
            )}
          </button>
          {error && (
            <p className="text-[12px] text-rose-300 mt-1">{error}</p>
          )}
        </div>
      </section>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {initialAnalysis && (
        <CommentaryBlock
          tone="sky"
          title="Yapıcı Analiz"
          subtitle="Kararın güçlü yönleri ve uygulanabilirliği"
          icon="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
        >
          {initialAnalysis}
        </CommentaryBlock>
      )}
      {initialCritique && (
        <CommentaryBlock
          tone="amber"
          title="Eleştirel Perspektif"
          subtitle="Karara yönelik risk ve karşı argümanlar"
          icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
        >
          {initialCritique}
        </CommentaryBlock>
      )}
    </div>
  );
}

function CommentaryBlock({
  tone,
  title,
  subtitle,
  icon,
  children,
}: {
  tone: "sky" | "amber";
  title: string;
  subtitle: string;
  icon: string;
  children: React.ReactNode;
}) {
  const TONES: Record<typeof tone, { tile: string; ring: string }> = {
    sky: {
      tile: "bg-sky-500/10 border-sky-500/30 text-sky-200",
      ring: "border-sky-500/15",
    },
    amber: {
      tile: "bg-amber-500/10 border-amber-500/30 text-amber-200",
      ring: "border-amber-500/15",
    },
  };
  const c = TONES[tone];
  return (
    <section
      className={[
        "rounded-2xl border bg-white/[0.02] p-5 lg:p-6",
        c.ring,
      ].join(" ")}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={[
            "w-9 h-9 rounded-xl border flex items-center justify-center",
            c.tile,
          ].join(" ")}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.7}
              d={icon}
            />
          </svg>
        </div>
        <div className="min-w-0">
          <h3 className="text-[14px] font-bold text-white">{title}</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div className="text-[13.5px] text-slate-200 leading-relaxed whitespace-pre-line">
        {children}
      </div>
    </section>
  );
}
